import React, { forwardRef, useState, useId } from "react";

// ============================================
// CLSX IMPORT
// ============================================
// Why clsx: It's a tiny utility (228B) for constructing className strings conditionally
// Benefits over manual string concatenation:
// - Cleaner syntax
// - Handles undefined/null/false values automatically
// - Supports objects, arrays, and mixed arguments
// - TypeScript support out of the box

import clsx from "clsx";

import styles from "./Input.module.scss";
import type { InputProps } from "./Input.types";

/**
 * Input Component
 *
 * A flexible, accessible input component with support for:
 * - Multiple variants (default, outlined, filled, borderless)
 * - Multiple sizes (sm, md, lg)
 * - Left and right icons
 * - Custom colors via CSS variables
 * - Error, success, and disabled states
 * - Labels and helper text
 */

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      // Icons
      leftIcon,
      rightIcon,

      //Styleing
      variant = "default",
      inputSize = "md",
      borderColor,
      borderSize = "md",
      bgColor,
      focusBorderColor,

      // State
      isError = false,
      isSuccess = false,
      isDisabled = false,

      // Content
      label,
      helperText,
      errorMessage,

      // Layout
      fullWidth = false,

      //Handlers
      onRightIconClick,
      onFocus,
      onBlur,

      // Rest
      className,
      id,
      ...rest
    },
    ref,
  ) => {
    // ============================================
    // STATE
    // ============================================

    // Track focus for styling
    const [isFocused, setIsFocused] = useState(false);

    // Generate unique id for accessiblility
    const generatedId = useId();
    const inputId = id || generatedId;

    // ============================================
    // CSS CUSTOM PROPERTIES
    // ============================================

    // Why: CSS variables allow runtime style customization
    // without creating new CSS classes
    const customStyles = {
      ...(borderColor && { "--input-border-color": borderColor }),
      ...(bgColor && { "--input-bg": bgColor }),
      ...(focusBorderColor && { "--input-border-focus": focusBorderColor }),
      ...(borderSize && { "--input-border-size": borderSize }),
    } as React.CSSProperties;

    const hasCustomStyles =
      borderColor || bgColor || focusBorderColor || borderSize;

    // ============================================
    // CLSX CLASS COMPOSITION
    // ============================================

    /**
     * Wrapper classes using clsx
     *
     * clsx syntax options:
     * 1. Strings: clsx('foo', 'bar') => 'foo bar'
     * 2. Objects: clsx({ foo: true, bar: false }) => 'foo'
     * 3. Arrays: clsx(['foo', 'bar']) => 'foo bar'
     * 4. Mixed: clsx('foo', { bar: true }, ['baz']) => 'foo bar baz'
     *
     * Why object syntax: Conditionally include classes based on props/state
     * Keys are class names, values are boolean conditions
     */

    const wrapperClasses = clsx(
      // Always include base wrapper styles
      styles.inputWrapper,
      // Conditional classes using object syntax
      // only included when condition is truthy
      {
        [styles.fullWidth]: fullWidth,
      },
      // External className passed as prop (allows customization)
      className,
    );

    /**
     * Container classes (the visual input box)
     * This demonstrates various clsx patterns
     */
    const containerClasses = clsx(
      // 1- Always-on base class
      styles.inputContainer,
      // 2- Dynamic class names using template literals
      // Constructs class name from variables

      styles[`variant-${variant}`], // e.g 'varient-default'
      styles[`size-${inputSize}`], // e.g 'size-md'
      styles[`borderSize-${borderSize}`],

      // 3. Object syntax for conditional classes
      {
        // Icon modifieers - adjust padding when icons present
        [styles.hasLeftIcon]: Boolean(leftIcon),
        [styles.hasRightIcon]: Boolean(rightIcon),

        // State modifiers
        [styles.isError]: isError,
        [styles.isSuccess]: isSuccess,
        [styles.isDisabled]: isDisabled,
        [styles.isFocused]: isFocused,

        // Layout modifiers
        [styles.fullWidth]: fullWidth,
      },
    );

    /**
     * Left icon classes
     * Demonstrates combining multiple conditions
     */
    const leftIconClasses = clsx(
      styles.icon, // Base icon styles
      styles.leftIcon, // position-specific styles
    );

    /**
     * Right icon classes
     */
    const rightIconClasses = clsx(styles.icon, styles.rightIcon, {
      [styles.clickable]: Boolean(onRightIconClick),
    });
    /**
     * Helper Text Classes
     */
    const helperTextClasses = clsx(styles.helperText, {
      [styles.errorText]: isError && errorMessage,
    });

    /**
     * Event Handlers
     */
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    // Handle keyboard events for icon accessiblity
    const handleIconKeyDown = (
      e: React.KeyboardEvent,
      handler?: () => void,
    ) => {
      if (handler && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
      }
    };
    // Render
    return (
      <div
        className={wrapperClasses}
        style={hasCustomStyles ? customStyles : undefined}
      >
        {/** Label */}
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
          </label>
        )}

        {/** Input Container */}
        <div className={containerClasses}>
          {/** Left Icon */}
          {leftIcon && <span className={leftIconClasses}>{leftIcon}</span>}

          {/** Input Element */}
          <input
            ref={ref}
            id={inputId}
            className={styles.input}
            disabled={isDisabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            aria-invalid={isError}
            aria-describedby={
              helperText || errorMessage ? `${inputId}-helper` : undefined
            }
            {...rest}
          />

          {/** Right icon */}
          {rightIcon && (
            <span
              className={rightIconClasses}
              onKeyDown={(e) => handleIconKeyDown(e, onRightIconClick)}
              onClick={onRightIconClick}
              role={onRightIconClick ? "button" : undefined}
              tabIndex={onRightIconClick ? 0 : undefined}
              aria-label={onRightIconClick ? "Right icon action" : undefined}
            >
              {rightIcon}
            </span>
          )}
        </div>
        {/* Helper/Error Text*/}
        {(helperText || errorMessage) && (
          <span
            id={`${inputId}-helper`}
            className={helperTextClasses}
            role={isError ? "alert" : undefined}
          >
            {isError && errorMessage ? errorMessage : helperText}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";
